import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { DialogService } from 'primeng/dynamicdialog';
import { EditorModule } from 'primeng/editor';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { MessageModule } from 'primeng/message';
import { SelectModule } from 'primeng/select';
import { TextareaModule } from 'primeng/textarea';
import { LogisticsProject, ProjectTemplate, PROJECT_TYPES, PROJECT_PRIORITIES, COMPLETION_METHODS } from '../models/logistics-project.model';
import { LogisticsProjectsService } from '../logistics-projects.service';
import { SessionStorageService } from '../../services/session-storage.service';
import { Location } from '../../location/location.model';
import { LocationService } from '../../location/location.service';
import { LazyLocationPickerDirective } from '../../shared/location-picker/lazy-location-picker.directive';
import { CreateLocationComponent } from '../../location/create-location/create-location.component';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import { PageDetailHeroComponent } from '../../shared/page-layout/page-detail-hero.component';
import { PageFormSectionComponent } from '../../shared/page-layout/page-form-section.component';
import { TranslocoPipe } from '@jsverse/transloco';
import { CommercialPartyPickerComponent } from '../../shared/commercial-party-picker/commercial-party-picker.component';
import { CommercialPartyReference, normalizeCommercialPartyReference } from '../../shared/commercial-party-picker/commercial-party.model';

@Component({
    selector: 'app-create-logistics-project',
    standalone: true,
    imports: [ CommonModule, FormsModule, RouterModule, ButtonModule, DatePickerModule, EditorModule, IftaLabelModule,
        InputGroupModule, InputGroupAddonModule, InputNumberModule, InputTextModule, MessageModule,
        SelectModule, LazyLocationPickerDirective, TextareaModule, PageShellComponent, PageDetailHeroComponent, PageFormSectionComponent, TranslocoPipe,
        CommercialPartyPickerComponent ],
    templateUrl: './create-logistics-project.component.html',
    styleUrls: ['./create-logistics-project.component.css'],
    providers: [DialogService]
})
export class CreateLogisticsProjectComponent implements OnInit {

    commercialPartyReference: CommercialPartyReference = { source: 'CRM' };

    private service = inject(LogisticsProjectsService);
    private session = inject(SessionStorageService);
    private route = inject(ActivatedRoute);
    private router = inject(Router);
    private locationService = inject(LocationService);
    private dialogService = inject(DialogService);

    isEditMode = false; projectId: number | null = null;
    owningCompanyId!: number; loading = false; saving = false; error: string | null = null;
    newProjectPersisted = false;

    project: LogisticsProject = { projectName: '', projectType: 'GENERAL_LOGISTICS', sharingMode: 'PRIVATE', status: 'DRAFT', priority: 'NORMAL', completionMethod: 'MANUAL', percentComplete: 0, collectProgress: false };
    sharingModes = [{ label: 'Private', value: 'PRIVATE' }, { label: 'All active sister companies', value: 'GROUP' }];
    projectTypes = PROJECT_TYPES; priorities = PROJECT_PRIORITIES; completionMethods = COMPLETION_METHODS;
    templates: ProjectTemplate[] = [];
    selectedTemplateCode?: string;
    templatesLoading = false;
    locations: Location[] = [];
    selectedLocation: Location | null = null;

    ngOnInit(): void {
        const c = this.session.getCustomer();
        if (c?.company?.id) this.owningCompanyId = c.company.id;
        else { this.error = 'No company context.'; return; }
        this.loadTemplates();
        const id = this.route.snapshot.paramMap.get('projectId');
        if (id) { this.isEditMode = true; this.projectId = +id; this.loadProject(); }
    }

    loadProject(): void { this.loading = true;
        this.service.getProject(this.projectId!, this.owningCompanyId).subscribe({
            next: d => {
                this.project = d;
                this.commercialPartyReference = normalizeCommercialPartyReference({
                    source: d.crmClientId ? 'CRM' : 'LEGACY',
                    crmClientId: d.crmClientId,
                    crmAccountId: d.crmAccountId,
                    crmContactId: d.crmClientContactId,
                    legacyCompanyId: d.customerCompanyId,
                    legacyCustomerId: d.customerContactId
                });
                this.loading = false;
                this.hydrateProjectReferences();
            },
            error: () => { this.error = 'Failed to load project.'; this.loading = false; }
        });
    }

    save(): void {
        if (this.saving || (!this.isEditMode && this.newProjectPersisted)) return;
        if (!this.project.projectName?.trim()) { this.error = 'Project name is required.'; return; }
        this.applySelectedReferences();
        this.saving = true; this.error = null;
        const isNewProject = !this.isEditMode;
        const req = !this.isEditMode && this.selectedTemplateCode
            ? this.service.createProjectFromTemplate(this.selectedTemplateCode, this.owningCompanyId, this.project)
            : this.isEditMode
            ? this.service.updateProject(this.projectId!, this.owningCompanyId, this.project)
            : this.service.createProject(this.owningCompanyId, this.project);
        req.subscribe({ next: s => void this.openSavedProject(s, isNewProject),
            error: e => { this.error = e?.error?.message || 'Failed to save.'; this.saving = false; } });
    }

    private async openSavedProject(project: LogisticsProject, isNewProject: boolean): Promise<void> {
        if (isNewProject) this.newProjectPersisted = true;
        if (!this.hasValidProjectId(project?.projectId)) {
            this.error = 'Project was saved, but its ID was not returned. Click Cancel to open the project list.';
            this.saving = false;
            return;
        }

        try {
            const navigationSucceeded = await this.router.navigate(['/logistics-projects', project.projectId]);
            if (!navigationSucceeded) {
                this.error = 'Project was saved, but could not be opened. Click Cancel to open the project list.';
            }
        } catch {
            this.error = 'Project was saved, but could not be opened. Click Cancel to open the project list.';
        } finally {
            this.saving = false;
        }
    }

    private hasValidProjectId(projectId?: number): projectId is number {
        return typeof projectId === 'number' && Number.isInteger(projectId) && projectId > 0;
    }

    cancel(): void { this.router.navigate(this.isEditMode ? ['/logistics-projects', this.projectId] : ['/logistics-projects']); }

    loadTemplates(): void {
        if (!this.owningCompanyId) return;
        this.templatesLoading = true;
        this.service.getTemplates(this.owningCompanyId, this.project.projectType).subscribe({
            next: templates => {
                this.templates = templates || [];
                this.templatesLoading = false;
            },
            error: () => {
                this.templates = [];
                this.templatesLoading = false;
            }
        });
    }

    onCommercialPartyChange(reference: CommercialPartyReference): void {
        const normalized = normalizeCommercialPartyReference(reference);
        this.commercialPartyReference = normalized;
        this.project.crmClientId = normalized.crmClientId ?? undefined;
        this.project.crmAccountId = normalized.crmAccountId ?? undefined;
        this.project.crmClientContactId = normalized.crmContactId ?? undefined;
        this.project.customerCompanyId = normalized.legacyCompanyId ?? undefined;
        this.project.customerContactId = normalized.legacyCustomerId ?? undefined;
    }

    onLocationSelected(location: Location | null): void {
        this.selectedLocation = location;
        this.project.sourceLocationId = location?.id;
        this.project.projectLocationText = location?.name;
    }

    onProjectTypeChanged(): void {
        this.selectedTemplateCode = undefined;
        this.loadTemplates();
    }

    onTemplateSelected(templateCode?: string): void {
        this.selectedTemplateCode = templateCode;
        const template = this.templates.find(item => item.templateCode === templateCode);
        if (!template || this.isEditMode) return;
        this.project.projectType = template.projectType || this.project.projectType;
        this.project.projectCategory = template.projectCategory || this.project.projectCategory;
        this.project.serviceProduct = template.serviceProduct || this.project.serviceProduct;
        this.project.operationalMode = template.operationalMode || this.project.operationalMode;
        this.project.completionMethod = template.completionMethod || this.project.completionMethod;
        this.project.priority = template.defaultPriority || this.project.priority;
        this.project.billingPolicy = template.defaultBillingPolicy || this.project.billingPolicy;
        this.project.revenueRecognitionPolicy = template.defaultRevenueRecognitionPolicy || this.project.revenueRecognitionPolicy;
        this.project.costingPolicy = template.defaultCostingPolicy || this.project.costingPolicy;
        if (!this.project.description && template.description) {
            this.project.description = template.description;
        }
    }

    openLocationDialog(): void {
        const ref = this.dialogService.open(CreateLocationComponent, {
            header: 'Create/Update Location',
            modal: true,
            width: '70%',
            data: { embedded: true },
            styleClass: 'shipment-location-dialog'
        });
        ref.onClose.subscribe((location: Location) => {
            if (!location) return;
            this.upsertLocationOption(location);
            this.onLocationSelected(location);
        });
    }

    locationMeta(location: Location): string {
        return [location.locationType, location.externalLocationReference].filter(Boolean).join(' | ');
    }

    private hydrateProjectReferences(): void {
        if (this.project.sourceLocationId) this.ensureLocationLoaded(this.project.sourceLocationId);
    }

    private applySelectedReferences(): void {
        this.project.sourceLocationId = this.selectedLocation?.id;
        this.project.projectLocationText = this.selectedLocation?.name || this.project.projectLocationText;
        this.project.defaultCurrencyCode = this.project.defaultCurrencyCode || 'SAR';
    }

    private reconcileLocationSelection(): void {
        if (this.project.sourceLocationId) {
            this.selectedLocation = this.locations.find(location => location.id === this.project.sourceLocationId)
                || this.selectedLocation;
        }
    }

    private ensureLocationLoaded(locationId: number): void {
        if (this.locations.some(location => location.id === locationId)) {
            this.reconcileLocationSelection();
            return;
        }
        this.locationService.getLocationById(locationId).subscribe({
            next: response => {
                const location = response.data?.[0];
                if (location) {
                    this.upsertLocationOption(location);
                    this.reconcileLocationSelection();
                }
            }
        });
    }

    private upsertLocationOption(location: Location): void {
        if (!location?.id) return;
        this.locations = [location, ...this.locations.filter(option => option.id !== location.id)];
    }
}
